/**
 * useProtocolsActions — memoised add/remove/select handlers shared by the
 * shortcut menu, the "/" command search, and the marketplace modal. Extracted
 * to keep ProtocolsButton focused on orchestration.
 */

import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { useCallback } from 'react';
import type { PinnedChip } from './evidence-settings-popover';
import type { ResolvedShortcut } from './shortcut-marketplace-data';

interface UseProtocolsActionsArgs {
  pinnedChips: PinnedChip[];
  marketShortcuts: ResolvedShortcut[];
  onAddProtocol?: (
    label: string,
    query: string,
    origin: 'library' | 'custom',
    description?: string
  ) => void;
  onRestoreDefaultShortcut?: (shortcutId: string) => void;
  onRemoveProtocol?: (chipId: string) => void;
  onPinnedChipClick?: (chip: PinnedChip) => void;
}

export function useProtocolsActions({
  pinnedChips,
  marketShortcuts,
  onAddProtocol,
  onRestoreDefaultShortcut,
  onRemoveProtocol,
  onPinnedChipClick,
}: UseProtocolsActionsArgs) {
  // Install a shortcut; restoring a dismissed default routes to the dedicated
  // restore handler (server keeps origin='default'), otherwise add as a pin.
  const handleMarketplaceAdd = useCallback(
    (shortcut: ResolvedShortcut, origin: 'library' | 'custom') => {
      // Shared save chokepoint for the compact menu, "/" search and marketplace.
      track({
        event_name: TrackingEventName.EvidenceShortcutSaveToggled,
        props: {
          action: 'saved',
          shortcut_id: shortcut.id,
          is_team: Boolean(shortcut.teamShortcutId),
        },
        options: { amplitude: false },
      });
      const isDefaultShortcut = marketShortcuts.find(
        (s) => s.id === shortcut.id && s.id.startsWith('default-')
      );
      if (isDefaultShortcut && onRestoreDefaultShortcut) {
        onRestoreDefaultShortcut(shortcut.id);
      } else {
        onAddProtocol?.(shortcut.label, shortcut.query, origin, shortcut.description);
      }
    },
    [onAddProtocol, onRestoreDefaultShortcut, marketShortcuts]
  );

  // Remove a shortcut by matching chip id (defaults) or label (custom).
  const handleMarketplaceRemove = useCallback(
    (shortcut: ResolvedShortcut) => {
      const matchingChip = pinnedChips.find(
        (chip) =>
          chip.id === shortcut.id ||
          (chip.label === shortcut.label && !shortcut.id.startsWith('default-'))
      );
      if (matchingChip) {
        // Unsave chokepoint for the "/" search and marketplace (the compact
        // menu unsaves directly via onRemoveShortcut — tracked there).
        track({
          event_name: TrackingEventName.EvidenceShortcutSaveToggled,
          props: {
            action: 'unsaved',
            shortcut_id: shortcut.id,
          },
          options: { amplitude: false },
        });
        onRemoveProtocol?.(matchingChip.id);
      }
    },
    [pinnedChips, onRemoveProtocol]
  );

  // Run a curated protocol shorthand — sends /{shorthand} to chat.
  const handleProtocolSelect = useCallback(
    (shorthand: string) => {
      onPinnedChipClick?.({
        id: `protocol-${shorthand}`,
        label: shorthand,
        query: `/${shorthand}`,
        category: 'plan',
      });
    },
    [onPinnedChipClick]
  );

  return { handleMarketplaceAdd, handleMarketplaceRemove, handleProtocolSelect };
}
